import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

import { extractLabels } from "./formatters";
import {
  DefaultAreas,
  Event,
  EventCreate,
  LabellerTimeline,
  LabellerDetails,
  ClientIdentifiedEvent,
  ReviewEvent,
} from "src/store/api/generatedApi";
import { AreaLabels } from "components/scrapEyeLabelling/types";
import { ExpandedOriginalEvent } from "../types";

export const canonicalInstant = (timestamp: string): string => {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/.exec(
      timestamp
    );
  if (!match) return timestamp;
  const instant = dayjs.utc(timestamp);
  if (!instant.isValid()) return timestamp;
  const fraction = match[7]?.replace(/0+$/, "") ?? "";
  return `${instant.unix()}${fraction ? `.${fraction}` : ""}`;
};

export const reviewContentKey = (
  event: { event_type: string; start: string; end: string },
  labels: { label_type: string; label: string }[]
): string =>
  JSON.stringify([
    event.event_type,
    canonicalInstant(event.start),
    canonicalInstant(event.end),
    labels
      .map(({ label_type, label }) => JSON.stringify([label_type, label]))
      .sort(),
  ]);

export const stableReviewEventId = (reviewEvent: ReviewEvent): string => {
  const contentKey = reviewContentKey(
    reviewEvent.merged.event,
    reviewEvent.merged.labels
  );
  if (reviewEvent.original_events.length === 0) {
    return encodeURIComponent(contentKey);
  }

  const sourceIds = reviewEvent.original_events
    .map((event) => event.id)
    .sort((left, right) => left - right);
  return encodeURIComponent(JSON.stringify(["source", sourceIds]));
};

export const dedupeReviewEvents = (
  reviewEvents: ReviewEvent[]
): ReviewEvent[] => {
  const seenSavedEvents = new Set<string>();
  return reviewEvents.filter((event) => {
    if (event.original_events.length > 0) return true;
    const key = stableReviewEventId(event);
    if (seenSavedEvents.has(key)) return false;
    seenSavedEvents.add(key);
    return true;
  });
};

export const dedupeClientIdentifiedEvents = (
  events: ClientIdentifiedEvent[]
): ClientIdentifiedEvent[] => [
  ...new Map(
    events.map((event) => [reviewContentKey(event.event, event.labels), event])
  ).values(),
];

export const expandOriginalEvent = (
  originalEvent: Event,
  defaultAreas: DefaultAreas,
  labellerDetails: LabellerDetails,
  mergedEventId?: string,
  hasConflict?: boolean
): Omit<ExpandedOriginalEvent, keyof AreaLabels> => {
  const start = dayjs.utc(originalEvent.data.start);
  const end = dayjs.utc(originalEvent.data.end);

  const { material, coMaterial, area, comment, mass, confidences } =
    extractLabels(
      originalEvent.labels.map((label) => label.data),
      originalEvent.data.event_type,
      defaultAreas
    );

  return {
    id: originalEvent.id.toString(),
    originalEventId: originalEvent.id,
    labellerDetails: labellerDetails,
    mergedEventId: mergedEventId ?? "",
    hasConflict: hasConflict ?? false,
    start,
    end,
    eventType: originalEvent.data.event_type,
    material,
    coMaterial,
    area,
    comment,
    mass,
    confidences,
    pairId: null,
  };
};

export const getEventToLabellerMapping = (
  labellerTimelines: LabellerTimeline[]
): Record<number, LabellerDetails> => {
  return labellerTimelines.reduce<Record<number, LabellerDetails>>(
    (acc, timeline) => {
      timeline.periods.forEach((period) => {
        period.events.forEach((event) => {
          acc[event.id] = timeline.labeller;
        });
      });
      return acc;
    },
    {}
  );
};

export const massEventsFromReviewEvents = (
  reviewEvents: ReviewEvent[]
): EventCreate[] =>
  reviewEvents.flatMap((reviewEvent) =>
    reviewEvent.mass_event ? [reviewEvent.mass_event] : []
  );
